import type { PowerCurveComparison } from '@painpal/contracts';
import { AXIS_TEXT, chartTooltip, dataIndexOf } from '../../lib/chartTheme';
import type { ECOption, LineSeriesOption } from '../../lib/echarts';
import { formatMinutesSeconds, formatWatts } from '../../lib/format';
import { plural } from '../../lib/plural';
import { colors } from '../../theme/tokens';
import type { Variant } from '../../ui/useMediaQuery';

export const CURVE_HEIGHT = 300;
export const CURVE_HEIGHT_PHONE = 220;
export const POWER_CURVE_LABEL = 'Power curve: this ride against your best';
const TICKS = [1, 5, 15, 60, 300, 1200, 3600, 7200, 18000] as const;
const BEST_OPACITY = 0.45;

type Pair = [number, number | null];
type MarkPointItem = NonNullable<NonNullable<LineSeriesOption['markPoint']>['data']>[number];

export function durationLabel(seconds: number): string {
  if (seconds < 60) return `${seconds}S`;
  if (seconds % 3600 === 0) return `${seconds / 3600}H`;
  if (seconds < 3600 && seconds % 60 === 0) return `${seconds / 60}M`;
  return formatMinutesSeconds(seconds);
}

export const curveTicks = (maxDuration: number): number[] => TICKS.filter((tick) => tick <= maxDuration);

const pairs = (durations: number[], values: (number | null)[]): Pair[] => durations.map((d, i): Pair => [d, values[i] ?? null]);

export function beatenPoints(curve: PowerCurveComparison): [number, number][] {
  return curve.durations.flatMap((duration, i): [number, number][] => {
    const ride = curve.ride[i] ?? null;
    const best = curve.best[i] ?? null;
    return ride !== null && best !== null && ride > best ? [[duration, ride]] : [];
  });
}

export function curveSummary(curve: PowerCurveComparison): string {
  if (curve.best.every((watts) => watts === null)) return 'NO EARLIER RIDES TO COMPARE YET';
  const beaten = beatenPoints(curve).length;
  return beaten === 0 ? 'NO NEW BESTS' : `BEATS YOUR BEST AT ${beaten} ${plural(beaten, 'DURATION')}`;
}

export function curveTooltipHtml(curve: PowerCurveComparison, index: number): string {
  const duration = curve.durations[index];
  if (duration === undefined) return '';
  return [`<b>${durationLabel(duration)}</b>`, `THIS RIDE ${formatWatts(curve.ride[index] ?? null)}`, `BEST ${formatWatts(curve.best[index] ?? null)}`].join('<br/>');
}

export function curveTooltip(curve: PowerCurveComparison): (params: unknown) => string {
  return (params) => curveTooltipHtml(curve, dataIndexOf(params));
}

function marker([duration, watts]: [number, number], size: number): MarkPointItem {
  return {
    name: 'New best', coord: [duration, watts], symbol: 'circle', symbolSize: size,
    itemStyle: { color: colors.paper, borderColor: colors.hot, borderWidth: 2 }, label: { show: false },
  };
}

export function powerCurveOption(curve: PowerCurveComparison, variant: Variant = 'desktop'): ECOption {
  const phone = variant === 'phone';
  const maxDuration = Math.max(1, ...curve.durations);
  const ticks = curveTicks(maxDuration);
  return {
    grid: { left: phone ? 34 : 44, right: phone ? 8 : 16, top: 16, bottom: 24 },
    xAxis: {
      type: 'log', logBase: 10, min: 1, max: maxDuration, splitLine: { show: false },
      axisLine: { lineStyle: { color: colors.ink } }, axisTick: { show: false, customValues: ticks },
      axisLabel: { ...AXIS_TEXT, customValues: ticks, formatter: durationLabel },
    },
    yAxis: {
      type: 'value', min: 0, splitNumber: 4, name: 'W', nameTextStyle: AXIS_TEXT,
      axisLine: { show: false }, axisTick: { show: false }, splitLine: { lineStyle: { color: colors.line } }, axisLabel: AXIS_TEXT,
    },
    tooltip: chartTooltip({ phone, formatter: curveTooltip(curve) }),
    series: [
      {
        name: 'Best', type: 'line', data: pairs(curve.durations, curve.best), showSymbol: false, connectNulls: false,
        lineStyle: { color: colors.ink, width: 2, opacity: BEST_OPACITY }, itemStyle: { color: colors.ink, opacity: BEST_OPACITY },
      },
      {
        name: 'This ride', type: 'line', data: pairs(curve.durations, curve.ride), showSymbol: false,
        lineStyle: { color: colors.hot, width: 2.5 }, itemStyle: { color: colors.hot },
        markPoint: { silent: true, data: beatenPoints(curve).map((point) => marker(point, phone ? 8 : 10)) },
      },
    ],
  };
}
